import type { FC } from 'react';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { message } from 'src/common/components/message';
import { publicLink } from 'src/common/const';
import { HelperIconBox } from 'src/components/helper-icon';
import { UploadLocalFile } from 'src/editor/component/upload-panel/upload-local-file';
import { Images } from 'src/image';
import type { Guest } from 'src/utils/import-guest-util';
import { readGuestListFromFile } from 'src/utils/import-guest-util';

interface Props {
  /** true为导入外部协作，false为导入空间成员，区别是文案不一样*/
  isGuest: boolean;
  onReceiveGuestList: (guestList: Guest[]) => void;
}
/** 导入外部协作者或者空间成员 */
export const ImportMember: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  return (
    <div className="next-modal px-7 pb-4 w-[480px]">
      <div className="flex justify-between mt-7 items-center">
        <div className="text-h2 ">
          {t('common.batch_import')}&nbsp;
          {props.isGuest ? t('common.external_collaborator') : t('common.space_member')}
        </div>
        <HelperIconBox className="text-grey4 text-t4" linkContainer link={publicLink.importGuest}>
          {t('components.import_member.help')}
        </HelperIconBox>
      </div>
      <div className="text-t3-medium mt-7">
        {__BUILD_IN__
          ? t('components.import_member.support_email')
          : t('components.import_member.support_phone')}
        &nbsp;
        {props.isGuest
          ? t('components.import_member.add_to_page')
          : t('components.import_member.add_to_space')}
      </div>
      <div className="text-t4 text-grey3 mt-2.5 flex items-center">
        <span className="w-1 h-1 rounded-full bg-grey3 ml-1 mr-1.5" />
        {t('components.import_member.file_format_tip')}
      </div>
      <div className="text-t4 text-grey3 flex items-center">
        <span className="w-1 h-1 rounded-full bg-grey3 ml-1 mr-1.5" />
        {t('components.import_member.line_format_prefix')}
        {__BUILD_IN__
          ? t('components.import_member.format_email')
          : t('components.import_member.format_phone')}
      </div>
      <div className="flex justify-between mt-5">
        <img className="w-[200px]" src={Images.importGuestLeft} />
        <img className="w-[200px]" src={Images.importGuestRight} />
      </div>
      <UploadLocalFile
        singleFile
        labelClassName="w-full"
        style={{ width: '100%' }}
        title={t('components.import_member.select_file')}
        onSelect={async (files) => {
          const file = files[0];
          if (!file) return;
          if (!file.name.endsWith('.txt') && !file.name.endsWith('.csv')) {
            message.warning(t('components.import_member.file_format_error'));
            return;
          }
          const id = message.loading({ content: t('components.import_member.parsing') });
          try {
            const guests = await readGuestListFromFile(file);
            if (guests.length === 0) {
              throw Error(t('components.import_member.empty_list_error'));
            }
            props.onReceiveGuestList(guests);
          } catch (e: any) {
            if (e instanceof Error) {
              message.error(e.message);
            } else {
              message.error(e);
            }
          } finally {
            message.closeMessage(id);
          }
        }}
        inputAttribute={{
          accept: '.csv,.txt',
        }}
      />
      <div className="text-t4 text-grey3 text-center mt-[-6px]">
        {t('components.import_member.file_limit_tip')}
      </div>
    </div>
  );
};
